import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  getAdminProperties,
  postPropertyToFacebook,
  blockProperty,
  unblockProperty,
  publishProperty,
  unpublishProperty,
} from '../../../api/services/adminService';
import './Properties.css';

const PAGE_SIZE = 20;

const formatScalar = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const StatusBadges = ({ active, published }) => (
  <div className="property-status-badges">
    <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
      {active ? 'Active' : 'Blocked'}
    </span>
    <span className={`status-badge ${published ? 'published' : 'unpublished'}`}>
      {published ? 'Published' : 'Unpublished'}
    </span>
  </div>
);

/** Segmented control: options are { id, label }; value is the active id. */
const FilterGroup = ({ label, options, value, onChange }) => (
  <div className="filter-group">
    <span className="filter-group-label">{label}</span>
    <div className="filter-chips">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`filter-chip ${value === o.id ? 'active' : ''}`}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

// Tri-state filters map UI id -> boolean|undefined for the backend query.
const ACTIVITY_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'blocked', label: 'Blocked' },
];
const VISIBILITY_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'published', label: 'Published' },
  { id: 'unpublished', label: 'Unpublished' },
];
const FACEBOOK_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'posted', label: 'Posted' },
  { id: 'not', label: 'Not posted' },
];

const Properties = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postingId, setPostingId] = useState(null);
  const [stateActionId, setStateActionId] = useState(null);
  const [confirmProperty, setConfirmProperty] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmSuccess, setConfirmSuccess] = useState(null);

  // Filters (mirror backend GET /api/admin/properties params)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [activity, setActivity] = useState('all'); // -> isActive
  const [visibility, setVisibility] = useState('all'); // -> published
  const [facebook, setFacebook] = useState('all'); // -> facebookPosted

  // Convert UI filter ids into the backend query object.
  const queryOpts = useMemo(() => {
    const opts = { page, size: PAGE_SIZE };
    if (search.trim()) opts.search = search.trim();
    if (ownerId.trim()) opts.ownerId = ownerId.trim();
    if (activity === 'active') opts.isActive = true;
    else if (activity === 'blocked') opts.isActive = false;
    if (visibility === 'published') opts.published = true;
    else if (visibility === 'unpublished') opts.published = false;
    if (facebook === 'posted') opts.facebookPosted = true;
    else if (facebook === 'not') opts.facebookPosted = false;
    return opts;
  }, [page, search, ownerId, activity, visibility, facebook]);

  const load = useCallback(async (opts) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminProperties(opts);
      const inner = res?.data;
      setPageData(inner || null);
      setList(Array.isArray(inner?.content) ? inner.content : []);
    } catch (e) {
      setError(e.message || 'Failed to load properties');
      setList([]);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(queryOpts);
  }, [queryOpts, load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput);
  };

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setOwnerId('');
    setActivity('all');
    setVisibility('all');
    setFacebook('all');
    setPage(0);
  };

  const hasActiveFilters =
    !!search || !!ownerId || activity !== 'all' || visibility !== 'all' || facebook !== 'all';

  const totalPages = pageData?.totalPages ?? 0;
  const totalElements = pageData?.totalElements ?? 0;
  const canPrev = page > 0;
  const canNext = totalPages > 0 && page < totalPages - 1;

  const confirmAndPost = async () => {
    if (!confirmProperty) return;
    const id = confirmProperty.id;
    setConfirmError(null);
    try {
      setConfirmBusy(true);
      setPostingId(id);
      const res = await postPropertyToFacebook(id);
      const successMsg = (res && (res.message || res.data || 'Posted to Facebook')) || `${confirmProperty.name || 'Property'} posted to Facebook`;
      setConfirmSuccess(successMsg);
      setConfirmError(null);
    } catch (e) {
      const msg = e?.message || (e?.response && (e.response.message || JSON.stringify(e.response))) || 'Failed to post property to Facebook';
      setConfirmError(msg);
      setConfirmSuccess(null);
    } finally {
      setConfirmBusy(false);
      setPostingId(null);
    }
  };

  const handleStateAction = async (property, action) => {
    const id = property.id;
    setStateActionId(id);
    try {
      if (action === 'block') await blockProperty(id);
      else if (action === 'unblock') await unblockProperty(id);
      else if (action === 'publish') await publishProperty(id);
      else if (action === 'unpublish') await unpublishProperty(id);
      await load(queryOpts);
    } catch (e) {
      setError(e?.message || `Failed to ${action} property`);
    } finally {
      setStateActionId(null);
    }
  };

  const openDetails = (property) => {
    navigate(`/system-admin/properties/${property.id}`);
  };

  return (
    <div className="properties-container">
      <div className="properties-header-row">
        <h1 className="page-title">Properties Management</h1>
        <form className="properties-search-form" onSubmit={handleSearch}>
          <input
            id="property-search-input"
            type="text"
            placeholder="Search by name, address, or owner email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
      </div>

      <div className="properties-filters">
        <FilterGroup label="Activity" options={ACTIVITY_OPTIONS} value={activity} onChange={(v) => { setActivity(v); setPage(0); }} />
        <FilterGroup label="Visibility" options={VISIBILITY_OPTIONS} value={visibility} onChange={(v) => { setVisibility(v); setPage(0); }} />
        <FilterGroup label="Facebook" options={FACEBOOK_OPTIONS} value={facebook} onChange={(v) => { setFacebook(v); setPage(0); }} />
        <div className="filter-group">
          <span className="filter-group-label">Owner ID</span>
          <input
            type="text"
            className="filter-owner-input"
            placeholder="Filter by owner UUID"
            value={ownerId}
            onChange={(e) => { setOwnerId(e.target.value); setPage(0); }}
          />
        </div>
        {hasActiveFilters ? (
          <button type="button" className="filter-reset" onClick={resetFilters}>
            Clear filters
          </button>
        ) : null}
      </div>

      {loading && <p className="properties-state">Loading properties…</p>}
      {!loading && error && <p className="properties-state properties-state-error">{error}</p>}

      {!loading && !error && list.length === 0 && (
        <p className="properties-state">No properties found.</p>
      )}

      {!loading && !error && list.length > 0 && (
        <div className="properties-table-wrap">
          <table className="properties-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Owner</th>
                <th>Units</th>
                <th>Occupancy</th>
                <th>Status</th>
                <th>Facebook</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((property) => {
                const loc = property.location || {};
                const contacts = property.contacts || {};
                const cityLine = [loc.area, loc.city].filter(Boolean).join(', ');
                const busy = stateActionId === property.id;
                return (
                  <tr key={property.id} className="property-row" onClick={() => openDetails(property)}>
                    <td data-label="Property">
                      <div className="cell-primary">{property.name || '—'}</div>
                      <div className="cell-secondary">{property.address || cityLine || '—'}</div>
                    </td>
                    <td data-label="Owner">
                      <div className="cell-secondary">{formatScalar(contacts.email)}</div>
                    </td>
                    <td data-label="Units">
                      <span className="cell-primary">{formatScalar(property.unitCount)}</span>
                    </td>
                    <td data-label="Occupancy">
                      {property.occupiedUnits != null ? (
                        <span className="cell-secondary">
                          {property.occupiedUnits}
                          {property.totalUnits != null ? ` / ${property.totalUnits}` : ''} occupied
                        </span>
                      ) : (
                        <span className="cell-secondary">—</span>
                      )}
                    </td>
                    <td data-label="Status">
                      <StatusBadges active={property.isActive} published={property.published} />
                    </td>
                    <td data-label="Facebook">
                      <span className={`fb-badge ${property.facebookPostId ? 'posted' : 'not'}`}>
                        {property.facebookPostId ? 'Posted' : 'Not posted'}
                      </span>
                    </td>
                    <td
                      data-label="Actions"
                      className="col-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => openDetails(property)}
                        >
                          View
                        </button>
                        {property.isActive ? (
                          <button
                            type="button"
                            className="btn btn-danger-soft btn-sm"
                            onClick={() => handleStateAction(property, 'block')}
                            disabled={busy}
                          >
                            {busy ? '…' : 'Block'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-success-soft btn-sm"
                            onClick={() => handleStateAction(property, 'unblock')}
                            disabled={busy}
                          >
                            {busy ? '…' : 'Unblock'}
                          </button>
                        )}
                        {property.published ? (
                          <button
                            type="button"
                            className="btn btn-secondary-soft btn-sm"
                            onClick={() => handleStateAction(property, 'unpublish')}
                            disabled={busy}
                          >
                            {busy ? '…' : 'Unpublish'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-success-soft btn-sm"
                            onClick={() => handleStateAction(property, 'publish')}
                            disabled={busy}
                          >
                            {busy ? '…' : 'Publish'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setConfirmProperty(property)}
                          disabled={postingId === property.id}
                        >
                          {postingId === property.id ? 'Posting…' : 'Facebook'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmProperty && createPortal(
        <div className="modal-overlay" role="presentation" onClick={() => { if (!confirmBusy) setConfirmProperty(null); }}>
          <div className="modal-content" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm post</h2>
              <button type="button" className="close-btn" onClick={() => { if (!confirmBusy) setConfirmProperty(null); }} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {!confirmSuccess ? (
                <>
                  <p>Are you sure you want to post <strong>{confirmProperty.name || 'this property'}</strong> to Facebook?</p>
                  {confirmError ? (
                    <div className="modal-error" role="alert">{confirmError}</div>
                  ) : null}
                </>
              ) : (
                <div className="modal-success" role="status">{confirmSuccess}</div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {!confirmSuccess ? (
                <>
                  <button type="button" className="btn" onClick={() => setConfirmProperty(null)} disabled={confirmBusy}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={confirmAndPost} disabled={confirmBusy}>{confirmBusy ? 'Posting…' : 'Post'}</button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" onClick={() => { setConfirmProperty(null); setConfirmSuccess(null); setConfirmError(null); }}>Close</button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {!loading && !error && list.length > 0 && (
        <div className="properties-pagination">
          <span className="properties-pagination-meta">
            {totalPages > 1
              ? `Page ${page + 1} of ${totalPages} (${totalElements} total)`
              : `${totalElements} propert${totalElements === 1 ? 'y' : 'ies'}`}
          </span>
          {totalPages > 1 ? (
            <div className="properties-pagination-actions">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default Properties;
